import { useMemo } from 'react';
import { AutocompleteOption } from '../components/core/Autocomplete';
import { getDistinctPropertyValues } from '../helpers/entity_property_helpers';
import { PASSING_ROUTES_PROP_KEY } from '../helpers/formatting_helpers';
import { useEntitiesFromStore } from './useEntities';
import { routeNameSortFn, useRouteNameLookup } from './useGtfs';

/**
 * Options for the values `prop` currently holds among the loaded entities, to
 * offer as suggestions when a value for that property is asked for.
 */
export const useEntityPropertyOptions = (
  prop?: ApiEntitySchemaProp,
): AutocompleteOption[] => {
  const entities = useEntitiesFromStore();
  const isPassingRoutes = prop?.key === PASSING_ROUTES_PROP_KEY;
  const routeNameLookup = useRouteNameLookup(isPassingRoutes);

  return useMemo(() => {
    if (!prop) {
      return [];
    }

    const values = getDistinctPropertyValues(entities, prop);

    if (!isPassingRoutes) {
      return values.map((value) => ({ value, label: value }));
    }
    return values.sort(routeNameSortFn).map((value) => ({
      value,
      label: routeNameLookup[value] ?? value,
      description: routeNameLookup[value] ? value : undefined,
    }));
  }, [entities, prop, isPassingRoutes, routeNameLookup]);
};
